import React, { useEffect, useState } from "react";

import {
  Table,
  Spinner,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
} from "reactstrap";
import axiosInstance from "../../Request";
import { URL } from "../../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const OverdueReporting = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);


  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [fintechList, setFintechList] = useState([]);
  const [selectedFintechId, setSelectedFintechId] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentLink, setPaymentLink] = useState("");


  // =================== Fetch Fintech/LSP List ===================
  useEffect(() => {
    const fetchFintechs = async () => {
      try {
        const res = await axiosInstance.get(URL.LSP_LIST);
        setFintechList((res.data || []).filter((f) => f.isActive));
      } catch (err) {
        console.error("Failed to fetch fintech list:", err);
      }
    };
    fetchFintechs();
  }, []);

  // =================== Fetch Overdue Report ===================
  const fetchOverdueReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (selectedFintechId) params.fintechId = selectedFintechId;

      const response = await axiosInstance.get(URL.OVERDUE_REPORT, { params });
      const data = Array.isArray(response.data) ? response.data : [];
      setRows(data);
    } catch (error) {
      console.error("Error fetching overdue report:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverdueReport();
  }, []);

  // =================== Export Excel ===================
  const exportToExcel = async () => {
    setExporting(true);
    try {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (selectedFintechId) params.fintechId = selectedFintechId;

      const response = await axiosInstance.get(URL.OVERDUE_REPORT_EXCEL, {
        responseType: "blob",
        params,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Overdue_Report.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Failed to download Excel");
    } finally {
      setExporting(false);
    }
  };

  // =================== Pay Modal ===================
  const openPayModal = (row) => {
    setSelectedRow(row);
    setAmount(row["Overdue Amount"]);
    setPaymentLink("");
    setModalOpen(true);
  };

  const generatePaymentLink = async () => {
    try {
      const userId = localStorage.getItem("user_id");
      const response = await axiosInstance.get(
        `${URL.PAYMENT_LINK_GENERATE}?customer_id=${selectedRow["Customer Id"]}&user_id=${userId}&amount=${amount}&flags=4`
      );
      if (response.data?.payInResponse?.success) {
        setPaymentLink(response.data.payInResponse.link);
        toast.success("Payment link generated!");
      } else {
        toast.error("Failed to generate payment link");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong!");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(paymentLink);
    toast.success("Payment link copied!");
  };

  // =================== Styles ===================
  const font12 = { fontSize: "10px" };
  const buttonSmStyle = {
    ...font12,
    padding: "4px 10px",
    height: "30px",
    lineHeight: "1",
  };

  const tableHeaders =
    rows.length > 0 && Array.isArray(rows) ? Object.keys(rows[0]) : [];

  return (
    <div style={font12}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* =================== Filter Bar =================== */}
      <div className="mb-3">
        {/* Row 1: Filters */}
        <div className="d-flex align-items-center mb-2" style={{ gap: 10 }}>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ ...buttonSmStyle, width: 150 }}
          />
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ ...buttonSmStyle, width: 150 }}
          />
          <Input
            type="select"
            value={selectedFintechId}
            onChange={(e) => setSelectedFintechId(e.target.value)}
            style={{ ...buttonSmStyle, width: 180 }}
          >
            <option value="">All Fintechs</option>
            {fintechList.map((f) => (
              <option key={f.id} value={f.id}>
                {f.brandName || f.name}
              </option>
            ))}
          </Input>
        </div>

        {/* Row 2: Action Buttons */}
        <div className="d-flex align-items-center" style={{ gap: 10 }}>
          <Button color="primary" style={buttonSmStyle} onClick={fetchOverdueReport}>
            Filter
          </Button>
          <Button
            color="success"
            onClick={exportToExcel}
            style={buttonSmStyle}
            disabled={exporting}
          >
            {exporting ? "Exporting..." : "Export To Excel"}
          </Button>
        </div>
      </div>

      {/* =================== Table =================== */}
      {loading ? (
        <div className="d-flex align-items-center mt-3" style={{ gap: 8 }}>
          <Spinner size="sm" />
          <span>Loading Overdue Report...</span>
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-danger">No overdue data found!</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <Table bordered striped hover responsive size="sm">
            <thead className="thead-light">
              <tr>
                <th style={font12}>#</th>
                {tableHeaders.map((header) => (
                  <th key={header} style={font12}>
                    {header}
                  </th>
                ))}
                <th style={font12}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td style={font12}>{index + 1}</td>
                  {tableHeaders.map((key) => (
                    <td key={key} style={font12}>
                      {row[key] !== null && row[key] !== undefined
                        ? row[key].toString()
                        : "-"}
                    </td>
                  ))}
                  <td style={font12}>
                    {Number(row["Overdue Amount"]) > 0 && (
                      <Button
                        color="success"
                        size="sm"
                        style={buttonSmStyle}
                        onClick={() => openPayModal(row)}
                      >
                        Pay
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* =================== Payment Modal =================== */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          Generate Payment Link
        </ModalHeader>
        <ModalBody>
          <Label>OverDue Amount</Label>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          {paymentLink && (
            <>
              <Label className="mt-3">Payment Link</Label>
              <Input value={paymentLink} disabled />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {!paymentLink ? (
            <Button color="primary" onClick={generatePaymentLink}>
              Generate Link
            </Button>
          ) : (
            <Button color="secondary" onClick={copyLink}>
              Copy Link
            </Button>
          )}
          <Button color="danger" onClick={() => setModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default OverdueReporting;
